import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../store';
import {
  useAnalysisAndFavorites,
  useStocksAndScreening,
} from '../hooks/useOptimizedSelector';
import { useKeyboardNavigation } from '../utils/keyboardNavigation';
import { formatScore } from '../utils/formatScore';
import { showGlobalMessage } from '../store/slices/globalMessageSlice';
import {
  openPanel,
  setSelectedTicker,
} from '../store/slices/analysisPanelSlice';
import { setChartTicker } from '../store/slices/chartSlice';
import {
  requestNavigateMainTab,
  requestDeepSubTab,
  setDeepAnalysisReturnTab,
} from '../store/slices/modalsSlice';
import {
  removeFavoriteThunk,
  updateFavoriteThunk,
  fetchPreviousResults,
  fetchFavoritesThunk,
} from '../store/thunks';
import { Favorite, StockData, FavoriteColor } from '../types';
import { authedFetch } from '../services/api';
import EmptyState from './common/EmptyState';
import StockActionModal from './StockActionModal';
interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
  format?: (value: any, favorite: Favorite) => string | JSX.Element;
  className?: string;
}

interface FavoritesListProps {
  favorites: Favorite[];
  onRemove?: (ticker: string) => void;
  onReorder?: (sourceIndex: number, destinationIndex: number) => void;
}

const FavoritesList: React.FC<FavoritesListProps> = ({
  favorites,
  onRemove,
  onReorder,
}) => {
  const dispatch = useAppDispatch();

  // Use optimized selectors to prevent cascade re-renders
  const { analysisPanel } = useAnalysisAndFavorites();
  const { stocks } = useStocksAndScreening();
  const [sortConfig, setSortConfig] = useState<{
    column: string | null;
    direction: 'asc' | 'desc';
  }>({ column: null, direction: 'asc' });
  const [selectedFavorites, setSelectedFavorites] = useState<Set<string>>(
    new Set()
  );
  const [currentRowIndex, setCurrentRowIndex] = useState<number>(-1);
  const [actionModal, setActionModal] = useState<{
    ticker: string;
    show: boolean;
  }>({ ticker: '', show: false });
  const [colorModal, setColorModal] = useState<{
    show: boolean;
    favorite: Favorite | null;
    newColor: FavoriteColor;
  }>({ show: false, favorite: null, newColor: 'blue' });
  const [stockDataCache, setStockDataCache] = useState<Map<string, StockData>>(
    new Map()
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySupportLines, setShowOnlySupportLines] = useState(false);

  // Ensure favorites is an array
  const favoritesList = useMemo(
    () => (Array.isArray(favorites) ? favorites : []),
    [favorites]
  );

  // Build stock data cache with case-insensitive matching
  useEffect(() => {
    const cache = new Map<string, StockData>();
    stocks.forEach((stock: StockData) => {
      // Store with both original case and uppercase for matching
      cache.set(stock.ticker.toUpperCase(), stock);
      cache.set(stock.ticker, stock);
    });
    setStockDataCache(cache);

    // If we have favorites but no stock data, try to fetch it
    if (favoritesList.length > 0 && stocks.length === 0) {
      dispatch(fetchPreviousResults());
    }
  }, [stocks, favoritesList.length, dispatch]);

  // BUG-14 fix 2026-05-09: favorites table previously showed all-N/A
  // for tickers not present in the latest screening results (e.g. user
  // favorited AAPL but the current screen only returned 10 different
  // tickers). Lazy-fetch per-ticker detail via /results/detail/<ticker>
  // (Spec #5 split-results endpoint added 2026-05-08) to fill the gap.
  // Code-review fix I2 (2026-05-09): track attempted tickers in a ref
  // so a `data: null` response (or any miss) doesn't keep retrying on
  // every cache update - that path was an infinite loop.
  const attemptedFavTickersRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    const missing = favoritesList
      .map((f) => f.ticker)
      .filter(
        (t) =>
          !stockDataCache.has(t.toUpperCase()) &&
          !stockDataCache.has(t) &&
          !attemptedFavTickersRef.current.has(t.toUpperCase())
      );
    if (missing.length === 0) return undefined;
    (async () => {
      const fetched: Array<[string, any]> = [];
      for (const ticker of missing.slice(0, 25)) { // cap concurrency
        attemptedFavTickersRef.current.add(ticker.toUpperCase());
        try {
          const resp = await authedFetch(`/results/detail/${encodeURIComponent(ticker)}`);
          if (!resp.ok) continue;
          const json = await resp.json();
          if (json?.data) fetched.push([ticker.toUpperCase(), json.data]);
        } catch {
          // Optional UI; never throw.
        }
      }
      if (cancelled || fetched.length === 0) return;
      setStockDataCache((prev) => {
        const next = new Map(prev);
        for (const [ticker, data] of fetched) {
          next.set(ticker, {
            ticker,
            current_price: data.current_price ?? null,
            day_change_percent: data.day_change_percent ?? null,
            ma_50: data.ma_50_value ?? null,
            ma_100: data.ma_100_value ?? null,
            ma_150: data.ma_150_value ?? null,
            ma_200: data.ma_200_value ?? null,
            atr_percentage: data.atr_percentage ?? null,
            score: data.score ?? null,
            company_name: data.company_name ?? null,
          } as any);
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [favoritesList, stockDataCache]);

  const getStockData = useCallback(
    (ticker: string): StockData | null => {
      // Try both original case and uppercase
      let stockData =
        stockDataCache.get(ticker) || stockDataCache.get(ticker.toUpperCase());
      return stockData || null;
    },
    [stockDataCache]
  );

  const getColorStyles = useCallback((color: string) => {
    const colorMap: {
      [key: string]: {
        backgroundColor: string;
        color: string;
        borderColor: string;
      };
    } = {
      green: {
        backgroundColor: '#198754',
        color: 'white',
        borderColor: '#198754',
      },
      yellow: {
        backgroundColor: '#ffc107',
        color: 'black',
        borderColor: '#ffc107',
      },
      red: {
        backgroundColor: '#dc3545',
        color: 'white',
        borderColor: '#dc3545',
      },
      blue: {
        backgroundColor: 'var(--accent-primary)',
        color: 'var(--on_primary)',
        borderColor: 'var(--accent-primary)',
      },
      purple: {
        backgroundColor: '#6f42c1',
        color: 'white',
        borderColor: '#6f42c1',
      },
    };
    return (
      colorMap[color] || {
        backgroundColor: '#6c757d',
        color: 'white',
        borderColor: '#6c757d',
      }
    );
  }, []);

  const copySelectedToClipboard = useCallback(async () => {
    if (selectedFavorites.size === 0) {
      dispatch(
        showGlobalMessage({
          message: 'Please select at least one favorite to copy',
          type: 'warning',
        })
      );
      return;
    }

    try {
      const tickers = Array.from(selectedFavorites).join(',');
      await navigator.clipboard.writeText(tickers);
      dispatch(
        showGlobalMessage({
          message: `📋 Copied ${selectedFavorites.size} selected tickers`,
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Clipboard copy error:', error);
      dispatch(
        showGlobalMessage({
          message: '❌ Failed to copy selected tickers',
          type: 'danger',
        })
      );
    }
  }, [dispatch, selectedFavorites]);

  const exportSelectedTickers = useCallback(() => {
    if (selectedFavorites.size === 0) {
      dispatch(
        showGlobalMessage({
          message: 'Please select at least one favorite to export',
          type: 'warning',
        })
      );
      return;
    }

    const selectedData = favoritesList.filter((fav) =>
      selectedFavorites.has(fav.ticker)
    );

    const headers = [
      'Ticker',
      'Price',
      'Day %',
      '150D MA',
      '200D MA',
      'ATR %',
      'Target %',
      'Score',
      'Entry Signal',
      'Notes',
    ];

    const rows = selectedData.map((favorite) => {
      const stockData = getStockData(favorite.ticker);
      return [
        favorite.ticker,
        stockData?.current_price?.toFixed(2) || '',
        stockData?.day_change_percent?.toFixed(2) || '',
        stockData?.ma_150?.toFixed(2) || '',
        stockData?.ma_200?.toFixed(2) || '',
        stockData?.atr_percentage?.toFixed(2) || '',
        stockData?.target_percent?.toFixed(1) || '',
        stockData?.score?.toString() || '',
        stockData?.entry_rationale || '',
        favorite.notes || '',
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `favorites_selected_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    dispatch(
      showGlobalMessage({
        message: `✅ Exported ${selectedFavorites.size} selected favorites`,
        type: 'success',
      })
    );
  }, [dispatch, favoritesList, getStockData, selectedFavorites]);

  const filteredFavorites = useMemo(() => {
    let filtered = favoritesList;

    if (showOnlySupportLines) {
      filtered = filtered.filter((favorite) => {
        const stockData = getStockData(favorite.ticker);
        return !!(
          stockData?.strong_support_result?.detected &&
          stockData?.strong_support_result?.support_line_coords
        );
      });
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((favorite) => {
        const stockData = getStockData(favorite.ticker);
        const ticker = favorite.ticker.toLowerCase();
        const company = stockData?.company_name?.toLowerCase() || '';
        return ticker.includes(q) || company.includes(q);
      });
    }

    return filtered;
  }, [favoritesList, getStockData, searchTerm, showOnlySupportLines]);

  const handleColorChangeInline = useCallback(
    async (favorite: Favorite, newColor: FavoriteColor) => {
      // Prevent duplicate updates
      if (favorite.color === newColor) {
        console.log(`Color is already ${newColor}, skipping update`);
        return;
      }

      try {
        console.log(
          `Updating ${favorite.ticker} color from ${favorite.color} to ${newColor}`
        );
        console.log('Favorite object:', favorite);
        console.log('Current favorites in state:', favoritesList);

        const result = await dispatch(
          updateFavoriteThunk({
            ticker: favorite.ticker,
            color: newColor,
            notes: favorite.notes || '',
          })
        ).unwrap();

        console.log('Update result from thunk:', result);

        // Force refresh favorites to get updated data
        await dispatch(fetchFavoritesThunk()).unwrap();
        console.log('Favorites refreshed after update');

        // Note: We don't show success message here because the thunk already shows one
      } catch (error: any) {
        console.error('Color update error:', error);
        // Only show error message if the thunk didn't already show one
        if (!error.message?.includes('Error updating')) {
          dispatch(
            showGlobalMessage({
              message: `❌ Failed to update color for ${favorite.ticker}: ${
                error.message || 'Unknown error'
              }`,
              type: 'danger',
            })
          );
        }
      }
    },
    [dispatch, favoritesList]
  );

  const handleRefreshData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      console.log('Refreshing stock data...');
      await dispatch(fetchPreviousResults()).unwrap();
      dispatch(
        showGlobalMessage({
          message: '✅ Stock data refreshed successfully!',
          type: 'success',
        })
      );
    } catch (error: any) {
      console.error('Failed to refresh data:', error);
      dispatch(
        showGlobalMessage({
          message: `❌ Failed to refresh data: ${
            error.message || 'Unknown error'
          }`,
          type: 'danger',
        })
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch]);

  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: 'color',
        label: 'Color',
        sortable: true,
        format: (_, favorite) => {
          const colorStyles = getColorStyles(favorite.color);
          return (
            <div className="tv-flex tv-items-center tv-gap-2">
              <select
                className="tv-form-select tv-form-select-sm"
                value={favorite.color}
                title={`Set color category for ${favorite.ticker}`}
                aria-label={`Set color category for ${favorite.ticker}`}
                style={{
                  width: '120px',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontWeight: '600',
                  backgroundColor: colorStyles.backgroundColor,
                  color: colorStyles.color,
                  borderColor: colorStyles.borderColor,
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  handleColorChangeInline(
                    favorite,
                    e.target.value as FavoriteColor
                  );
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <option
                  value="green"
                  style={{
                    backgroundColor: 'var(--surface_container_low)',
                    color: 'var(--text-primary)',
                  }}
                >
                  🟢 Green
                </option>
                <option
                  value="blue"
                  style={{
                    backgroundColor: 'var(--surface_container_low)',
                    color: 'var(--text-primary)',
                  }}
                >
                  🔵 Blue
                </option>
                <option
                  value="yellow"
                  style={{
                    backgroundColor: 'var(--surface_container_low)',
                    color: 'var(--text-primary)',
                  }}
                >
                  🟡 Yellow
                </option>
                <option
                  value="red"
                  style={{
                    backgroundColor: 'var(--surface_container_low)',
                    color: 'var(--text-primary)',
                  }}
                >
                  🔴 Red
                </option>
                <option
                  value="purple"
                  style={{
                    backgroundColor: 'var(--surface_container_low)',
                    color: 'var(--text-primary)',
                  }}
                >
                  🟣 Purple
                </option>
              </select>
            </div>
          );
        },
      },
      {
        key: 'ticker',
        label: 'Ticker',
        sortable: true,
        className: 'tv-font-mono tv-font-semibold',
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          return (
            <div className="tv-flex tv-items-center tv-gap-2">
              <span
                className="tv-font-mono tv-font-semibold"
                style={{
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                }}
              >
                {favorite.ticker}
              </span>
              {stockData?.exchange && (
                <span
                  className="tv-badge tv-text-xs"
                  style={{
                    backgroundColor:
                      stockData.exchange === 'NYSE'
                        ? 'var(--accent-bullish)'
                        : stockData.exchange === 'NASDAQ'
                          ? 'var(--accent-primary)'
                          : 'var(--surface_container_low)',
                    color: '#1a1a1a',
                    fontWeight: '600',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.625rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {stockData.exchange}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'current_price',
        label: 'Price',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          if (!stockData?.current_price) {
            return (
              <span
                className="tv-text-tertiary"
                title="Stock data not available in current screening results"
              >
                N/A
              </span>
            );
          }

          // Distance from the 200-day MA (close-based), not a daily price change.
          // Use the backend ma_200_distance_pct so it matches the screened value;
          // do not re-derive from the now-LIVE current_price.
          const ma200DistancePct = stockData.ma_200_distance_pct ?? 0;

          return (
            <div className="tv-flex tv-flex-col">
              <span>${stockData.current_price.toFixed(2)}</span>
              {ma200DistancePct !== 0 && (
                <small
                  className={`tv-text-xs ${
                    ma200DistancePct >= 0 ? 'tv-text-bull' : 'tv-text-bear'
                  }`}
                >
                  {ma200DistancePct >= 0 ? '+' : ''}
                  {ma200DistancePct.toFixed(1)}%
                </small>
              )}
            </div>
          );
        },
      },
      {
        key: 'day_change_percent',
        label: 'Day %',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          if (
            stockData?.day_change_percent === undefined ||
            stockData?.day_change_percent === null
          ) {
            return (
              <span
                className="tv-text-tertiary"
                title="Stock data not available in current screening results"
              >
                N/A
              </span>
            );
          }
          const value = Number(stockData.day_change_percent) || 0;
          return (
            <span className={value >= 0 ? 'tv-text-bull' : 'tv-text-bear'}>
              {value >= 0 ? '+' : ''}
              {value.toFixed(2)}%
            </span>
          );
        },
      },
      {
        key: 'ma_150',
        label: '150D MA',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          return stockData?.ma_150 ? (
            `$${stockData.ma_150.toFixed(2)}`
          ) : (
            <span
              className="tv-text-tertiary"
              title="Stock data not available in current screening results"
            >
              N/A
            </span>
          );
        },
      },
      {
        key: 'ma_200',
        label: '200D MA',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          return stockData?.ma_200 ? (
            `$${stockData.ma_200.toFixed(2)}`
          ) : (
            <span
              className="tv-text-tertiary"
              title="Stock data not available in current screening results"
            >
              N/A
            </span>
          );
        },
      },
      {
        key: 'atr_percentage',
        label: 'ATR %',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          return stockData?.atr_percentage ? (
            `${stockData.atr_percentage.toFixed(2)}%`
          ) : (
            <span
              className="tv-text-tertiary"
              title="Stock data not available in current screening results"
            >
              N/A
            </span>
          );
        },
      },
      {
        key: 'target_percent',
        label: 'Target %',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          if (!stockData?.target_percent) {
            return (
              <span
                className="tv-text-tertiary"
                title="Stock data not available in current screening results"
              >
                N/A
              </span>
            );
          }
          const value = stockData.target_percent;
          return (
            <span
              className={`tv-font-medium ${
                value >= 10
                  ? 'tv-text-bull'
                  : value >= 5
                    ? 'tv-text-warning'
                    : 'tv-text-secondary'
              }`}
            >
              {value.toFixed(1)}%
            </span>
          );
        },
      },
      {
        key: 'score',
        label: 'Score',
        sortable: true,
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          if (stockData?.score === undefined || stockData?.score === null) {
            return (
              <span
                className="tv-text-tertiary"
                title="Stock data not available in current screening results"
              >
                N/A
              </span>
            );
          }
          const score = Number(stockData.score);
          const className =
            score >= 7
              ? 'tv-text-bull'
              : score >= 4
                ? 'tv-text-warning'
                : 'tv-text-bear';
          return (
            <span className={`tv-font-medium ${className}`}>
              {formatScore(score)}
            </span>
          );
        },
      },
      {
        key: 'entry_rationale',
        label: 'Entry Signal',
        sortable: true,
        className: 'tv-text-sm',
        format: (_, favorite) => {
          const stockData = getStockData(favorite.ticker);
          if (!stockData?.entry_rationale) {
            return (
              <span
                className="tv-text-tertiary"
                title="Entry signal not available in current screening results"
              >
                N/A
              </span>
            );
          }
          const text = stockData.entry_rationale;
          return (
            <div
              className="tv-text-left"
              style={{ maxWidth: 220, minWidth: 0 }}
            >
              <span className="tv-block tv-truncate" title={text}>
                {text}
              </span>
            </div>
          );
        },
      },
      {
        key: 'notes',
        label: 'Notes',
        sortable: false,
        format: (_, favorite) => {
          return favorite.notes ? (
            <div style={{ maxWidth: 200, minWidth: 0 }}>
              <span
                className="tv-block tv-truncate tv-text-secondary tv-text-sm tv-text-left"
                title={favorite.notes}
              >
                {favorite.notes}
              </span>
            </div>
          ) : (
            <span className="tv-text-tertiary tv-text-sm fst-italic">
              No notes
            </span>
          );
        },
      },
    ],
    [getStockData, handleColorChangeInline, getColorStyles]
  );

  const handleSort = useCallback(
    (column: string) => {
      const direction =
        sortConfig.column === column && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc';
      setSortConfig({ column, direction });
    },
    [sortConfig]
  );

  const sortedFavorites = useMemo(() => {
    if (!sortConfig.column) return filteredFavorites;

    return [...filteredFavorites].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortConfig.column === 'ticker') {
        aValue = a.ticker;
        bValue = b.ticker;
      } else if (sortConfig.column === 'notes') {
        aValue = a.notes || '';
        bValue = b.notes || '';
      } else {
        // For stock data columns
        const aStock = getStockData(a.ticker);
        const bStock = getStockData(b.ticker);

        switch (sortConfig.column) {
          case 'current_price':
            aValue = aStock?.current_price || 0;
            bValue = bStock?.current_price || 0;
            break;
          case 'day_change_percent':
            aValue = aStock?.day_change_percent || 0;
            bValue = bStock?.day_change_percent || 0;
            break;
          case 'ma_150':
            aValue = aStock?.ma_150 || 0;
            bValue = bStock?.ma_150 || 0;
            break;
          case 'ma_200':
            aValue = aStock?.ma_200 || 0;
            bValue = bStock?.ma_200 || 0;
            break;
          case 'atr_percentage':
            aValue = aStock?.atr_percentage || 0;
            bValue = bStock?.atr_percentage || 0;
            break;
          case 'target_percent':
            aValue = aStock?.target_percent || 0;
            bValue = bStock?.target_percent || 0;
            break;
          case 'score':
            aValue = aStock?.score || 0;
            bValue = bStock?.score || 0;
            break;
          case 'entry_rationale':
            aValue = aStock?.entry_rationale || '';
            bValue = bStock?.entry_rationale || '';
            break;
          default:
            return 0;
        }
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  }, [filteredFavorites, sortConfig, getStockData]);

  const getSortIcon = useCallback(
    (column: string) => {
      if (sortConfig.column !== column) {
        return <i className="fas fa-sort tv-text-tertiary"></i>;
      }
      return sortConfig.direction === 'asc' ? (
        <i className="fas fa-sort-up tv-text-primary"></i>
      ) : (
        <i className="fas fa-sort-down tv-text-primary"></i>
      );
    },
    [sortConfig]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedFavorites(new Set(sortedFavorites.map((fav) => fav.ticker)));
      } else {
        setSelectedFavorites(new Set());
      }
    },
    [sortedFavorites]
  );

  const handleSelectFavorite = useCallback(
    (ticker: string, checked: boolean) => {
      setSelectedFavorites((prev) => {
        const newSelected = new Set(prev);
        if (checked) {
          newSelected.add(ticker);
        } else {
          newSelected.delete(ticker);
        }
        return newSelected;
      });
    },
    []
  );

  const handleRemoveFavorite = useCallback(
    (ticker: string) => {
      if (window.confirm(`Remove ${ticker} from favorites?`)) {
        dispatch(removeFavoriteThunk(ticker));
      }
    },
    [dispatch]
  );

  const handleRowClick = useCallback(
    (favorite: Favorite, event: React.MouseEvent, index: number) => {
      // Don't trigger if clicking on checkbox, buttons, or color badge
      const target = event.target as HTMLElement;
      if (
        target.closest('input[type="checkbox"]') ||
        target.closest('button') ||
        target.closest('.badge') ||
        target.closest('select')
      ) {
        return;
      }

      // Set current row index for highlighting (but don't select checkbox)
      setCurrentRowIndex(index);
      // Don't set selectedFavorites - this would select the checkbox
      // setSelectedFavorites(new Set([favorite.ticker])); // REMOVED

      // Open analysis panel preserving current tab
      dispatch(openPanel({ ticker: favorite.ticker, source: 'favorites' })); // Let panel preserve current tab
    },
    [dispatch]
  );

  // Sync selected row with analysis panel (moved after sortedFavorites definition)
  useEffect(() => {
    if (analysisPanel.isOpen && analysisPanel.selectedTicker) {
      setSelectedFavorites(new Set([analysisPanel.selectedTicker]));
      // Find and set current row index
      const rowIndex = sortedFavorites.findIndex(
        (fav) => fav.ticker === analysisPanel.selectedTicker
      );
      if (rowIndex !== -1) {
        setCurrentRowIndex(rowIndex);
      }
    }
  }, [analysisPanel.isOpen, analysisPanel.selectedTicker, sortedFavorites]);

  // Optimized keyboard navigation for favorites
  useKeyboardNavigation(
    currentRowIndex,
    sortedFavorites.length,
    setCurrentRowIndex,
    (newIndex: number) => {
      const selectedFavorite = sortedFavorites[newIndex];
      if (selectedFavorite) {
        const selectedTicker = selectedFavorite.ticker;
        setSelectedFavorites(new Set([selectedTicker]));
        dispatch(openPanel({ ticker: selectedTicker, source: 'favorites' }));
      }
    },
    (index: number) => `tr[data-ticker="${sortedFavorites[index]?.ticker}"]`,
    { enabled: sortedFavorites.length > 0 }
  );

  const handleColorChange = useCallback(async () => {
    if (!colorModal.favorite) return;

    try {
      await dispatch(
        updateFavoriteThunk({
          ticker: colorModal.favorite.ticker,
          color: colorModal.newColor,
          notes: colorModal.favorite.notes,
        })
      ).unwrap();

      setColorModal({ show: false, favorite: null, newColor: 'blue' });
      dispatch(
        showGlobalMessage({
          message: `✅ ${colorModal.favorite.ticker} color updated to ${colorModal.newColor}`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showGlobalMessage({
          message: `❌ Failed to update color for ${colorModal.favorite.ticker}`,
          type: 'danger',
        })
      );
    }
  }, [dispatch, colorModal]);

  if (favoritesList.length === 0) {
    return (
      <div className="tv-card">
        <EmptyState
          icon="fas fa-star"
          title="No Favorites Yet"
          description="Add stocks to your favorites from the screening results to track them with enhanced data and quick access to analysis tools."
        />
      </div>
    );
  }

  // Mobile card view component - iOS native style
  const MobileFavoriteCard: React.FC<{
    favorite: (typeof sortedFavorites)[0];
    index: number;
  }> = ({ favorite, index }) => {
    const stockData = getStockData(favorite.ticker);
    const isSelected = selectedFavorites.has(favorite.ticker);
    const colorStyles = getColorStyles(favorite.color);
    const isPositive = (stockData?.day_change_percent ?? 0) >= 0;

    return (
      <div
        className={`md:hidden mx-3 my-1.5 rounded-2xl cursor-pointer transition-all duration-200 active:scale-[0.98] ${
          isSelected
            ? 'ring-2 ring-yellow-500 bg-yellow-500/10'
            : 'bg-surface-container-high'
        }`}
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
        onClick={(e) => handleRowClick(favorite, e, index)}
      >
        <div className="p-4">
          <div className="flex justify-between items-start">
            {/* Left: Ticker + Color */}
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-lg tracking-tight">
                {favorite.ticker}
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                style={{
                  backgroundColor: colorStyles.backgroundColor + '30',
                  color: colorStyles.color,
                  border: `1px solid ${colorStyles.color}40`,
                }}
              >
                {favorite.color}
              </span>
              {stockData?.exchange && (
                <span className="text-[9px] bg-surface-container-highest text-text-muted px-1.5 py-0.5 rounded font-medium">
                  {stockData.exchange.slice(0, 3)}
                </span>
              )}
            </div>

            {/* Right: Action Buttons - tiny compact like VS Code */}
            <div className="flex gap-1">
              <button
                className="w-5 h-5 rounded flex items-center justify-center text-[#c9a227] active:text-[#ffd60a]"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setSelectedTicker(favorite.ticker));
                  dispatch(setChartTicker(favorite.ticker));
                  dispatch(setDeepAnalysisReturnTab('screening'));
                  dispatch(requestDeepSubTab('analysis'));
                  dispatch(requestNavigateMainTab('deep_analysis'));
                }}
                title="Deep Analysis"
              >
                <i className="fas fa-microscope text-[9px]"></i>
              </button>
              <button
                className="w-5 h-5 rounded flex items-center justify-center text-[#c9a227] active:text-[#ffd60a]"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    openPanel({ ticker: favorite.ticker, source: 'favorites' })
                  );
                }}
                title="Analyze"
              >
                <i className="fas fa-chart-line text-[9px]"></i>
              </button>
              <button
                className="w-5 h-5 rounded flex items-center justify-center text-[#ff6b6b] active:text-red-400"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFavorite(favorite.ticker);
                }}
                title="Remove"
              >
                <i className="fas fa-trash text-[9px]"></i>
              </button>
            </div>
          </div>

          {/* Stock Data */}
          {stockData ? (
            <div className="mt-3 flex items-end justify-between">
              <div className="flex gap-4">
                <div>
                  <span className="text-xs md:text-sm uppercase tracking-wider text-[#636366]">
                    Price
                  </span>
                  <div className="font-semibold text-white text-base font-mono">
                    ${stockData.current_price?.toFixed(2) || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-xs md:text-sm uppercase tracking-wider text-[#636366]">
                    Change
                  </span>
                  <div
                    className={`text-sm font-medium font-mono ${isPositive ? 'text-[#32d74b]' : 'text-[#ff453a]'}`}
                  >
                    {isPositive ? '+' : ''}
                    {stockData.day_change_percent?.toFixed(2) || '0.00'}%
                  </div>
                </div>
                <div>
                  <span className="text-xs md:text-sm uppercase tracking-wider text-[#636366]">
                    Score
                  </span>
                  <div
                    className={`text-sm font-bold font-mono ${
                      (stockData.score || 0) >= 7
                        ? 'text-[#32d74b]'
                        : (stockData.score || 0) >= 4
                          ? 'text-[#ffd60a]'
                          : 'text-[#ff453a]'
                    }`}
                  >
                    {formatScore(stockData.score, 'N/A')}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs md:text-sm uppercase tracking-wider text-[#636366]">
                  200D MA
                </span>
                <div className="text-sm text-white font-mono">
                  ${stockData.ma_200?.toFixed(2) || 'N/A'}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-[#8e8e93]">
              No stock data - run screening
            </div>
          )}

          {/* Notes */}
          {favorite.notes && (
            <div className="mt-2 text-xs text-text-muted bg-surface-container px-2 py-1.5 rounded-lg truncate">
              {favorite.notes}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full min-w-0 flex-1 min-h-0 flex-col">
      <div className="tv-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div
          className="tv-card-header tv-flex tv-justify-between tv-items-center tv-p-2 md:tv-p-4"
          style={{
            backgroundColor: 'var(--surface_container_high)',
            borderLeft: '3px solid var(--accent-primary)',
            borderRadius: 'var(--tv-radius-lg) var(--tv-radius-lg) 0 0',
          }}
        >
          <div className="tv-flex tv-items-center tv-gap-3">
            <div
              className="tv-p-2 tv-rounded"
              style={{ backgroundColor: 'var(--accent-primary-muted)' }}
            >
              <i className="fas fa-star" style={{ color: 'var(--accent-primary)' }}></i>
            </div>
            <div>
              <h5
                className="tv-font-semibold text-sm md:text-base"
                style={{ color: 'var(--text-primary)', margin: 0 }}
              >
                <span className="md:hidden">Favs</span>
                <span className="hidden md:inline">My Favorites</span>
              </h5>
              <div
                className="hidden md:block tv-text-xs md:tv-text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                <span>
                  {sortedFavorites.length} visible / {favoritesList.length}{' '}
                  total
                </span>
                <span> • </span>
                {selectedFavorites.size} selected
                <span> • Use ↑↓ arrows to navigate</span>
              </div>
            </div>
          </div>

          {/* Desktop Action Buttons - hidden on mobile */}
          <div className="hidden md:block">
            <div className="tv-flex tv-gap-2 tv-items-center tv-flex-wrap tv-justify-end">
              <div className="tv-flex tv-items-center tv-gap-2">
                <i className="fas fa-search tv-text-tertiary flex-shrink-0"></i>
                <input
                  type="text"
                  className="tv-form-control tv-form-control-sm"
                  style={{ width: '180px', paddingLeft: '8px' }}
                  placeholder="Search..."
                  title="Search favorites"
                  aria-label="Search favorites"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1"
                onClick={() => setShowOnlySupportLines(!showOnlySupportLines)}
                style={{
                  backgroundColor: showOnlySupportLines
                    ? 'var(--accent-info)'
                    : 'var(--surface_container_highest)',
                  color: showOnlySupportLines ? 'var(--on_primary)' : 'var(--text-primary)',
                  border: showOnlySupportLines
                    ? '2px solid var(--accent-info)'
                    : '1px solid var(--border-default)',
                }}
                title={
                  showOnlySupportLines
                    ? 'Showing only stocks with support lines'
                    : 'Show only favorites with support lines'
                }
              >
                <i
                  className={`fas ${
                    showOnlySupportLines ? 'fa-filter' : 'fa-chart-line'
                  }`}
                ></i>{' '}
                {showOnlySupportLines ? 'Support Only' : 'Support Filter'}
              </button>
              {/* Grok All button removed - Grok available only in Deep Analysis tab */}
              <div className="tv-flex tv-gap-1">
                <button
                  className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    padding: '5px 8px',
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSelectAll(true)}
                  title="Select all visible favorites"
                >
                  <i className="fas fa-check-square"></i> All
                </button>
                <button
                  className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1"
                  style={{
                    backgroundColor:
                      selectedFavorites.size === 0
                        ? 'var(--surface_container)'
                        : 'var(--surface_container_highest)',
                    color:
                      selectedFavorites.size === 0
                        ? 'var(--text-muted)'
                        : 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                    padding: '5px 8px',
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}
                  disabled={selectedFavorites.size === 0}
                  onClick={() => handleSelectAll(false)}
                  title="Clear selected favorites"
                >
                  <i className="fas fa-square"></i> None
                </button>
              </div>
              <button
                className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1"
                style={{
                  backgroundColor: 'var(--surface_container_highest)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '0.6875rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
                onClick={handleRefreshData}
                disabled={isRefreshing}
                title="Refresh stock data from latest screening results"
              >
                <i
                  className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`}
                ></i>
                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
              </button>
              <button
                className="tv-btn tv-btn-sm tv-flex tv-items-center tv-gap-1"
                style={{
                  backgroundColor:
                    selectedFavorites.size === 0
                      ? 'var(--surface_container)'
                      : 'var(--accent-info)',
                  color: selectedFavorites.size === 0 ? 'var(--text-muted)' : 'var(--on_primary)',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '0.6875rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
                onClick={copySelectedToClipboard}
                disabled={selectedFavorites.size === 0}
                title={`Copy ${selectedFavorites.size} selected tickers`}
              >
                <i className="fas fa-copy"></i> Copy ({selectedFavorites.size})
              </button>
              <button
                className="tv-btn tv-btn-success tv-btn-sm tv-flex tv-items-center tv-gap-1"
                style={{
                  backgroundColor:
                    selectedFavorites.size === 0
                      ? 'var(--surface_container)'
                      : 'var(--accent-bullish)',
                  color: selectedFavorites.size === 0 ? 'var(--text-muted)' : '#ffffff',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '0.6875rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
                onClick={exportSelectedTickers}
                disabled={selectedFavorites.size === 0}
                title={`Export ${selectedFavorites.size} selected favorites`}
              >
                <i className="fas fa-download"></i> Export (
                {selectedFavorites.size})
              </button>
            </div>
          </div>

          {/* Mobile Action Buttons - visible only on mobile */}
          <div className="md:hidden flex flex-wrap gap-1 mt-2">
            {/* Search - compact on mobile */}
            <div className="flex items-center gap-1 bg-surface-container rounded-lg px-2 py-1 flex-1 min-w-[120px]">
              <i className="fas fa-search text-[#aeaeb2] text-[10px] flex-shrink-0"></i>
              <input
                type="text"
                className="bg-transparent border-none text-white text-[11px] flex-1 min-w-0 outline-none placeholder-[#636366]"
                placeholder="Search..."
                title="Search favorites"
                aria-label="Search favorites"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Action buttons - tiny compact icons like VS Code */}
            <button
              className="w-6 h-6 rounded flex items-center justify-center text-text-muted bg-transparent active:bg-surface-container-highest"
              onClick={() => setShowOnlySupportLines(!showOnlySupportLines)}
              title={showOnlySupportLines ? 'Support Only' : 'Support Filter'}
            >
              <i
                className={`fas text-[10px] ${showOnlySupportLines ? 'fa-filter' : 'fa-chart-line'}`}
              ></i>
            </button>
            {/* Grok All removed */}
            <button
              className="w-6 h-6 rounded flex items-center justify-center text-text-muted bg-transparent active:bg-surface-container-highest"
              onClick={() => handleSelectAll(true)}
              title="Select All"
            >
              <i className="fas fa-check-square text-[10px]"></i>
            </button>
            <button
              className="w-6 h-6 rounded flex items-center justify-center text-text-muted bg-transparent active:bg-surface-container-highest"
              onClick={handleRefreshData}
              disabled={isRefreshing}
              title="Refresh Data"
            >
              <i
                className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''} text-[10px]`}
              ></i>
            </button>
            <button
              className="w-6 h-6 rounded flex items-center justify-center text-text-muted bg-transparent active:bg-surface-container-highest"
              onClick={copySelectedToClipboard}
              disabled={selectedFavorites.size === 0}
              title={`Copy (${selectedFavorites.size})`}
            >
              <i className="fas fa-copy text-[10px]"></i>
            </button>
            <button
              className="w-6 h-6 rounded flex items-center justify-center text-accent-bullish bg-transparent active:bg-accent-bullish-muted"
              onClick={exportSelectedTickers}
              disabled={selectedFavorites.size === 0}
              title={`Export (${selectedFavorites.size})`}
            >
              <i className="fas fa-download text-[10px]"></i>
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:overflow-x-auto max-md:overflow-y-auto overscroll-contain">
          {/* Data availability notice */}
          {stocks.length === 0 && favoritesList.length > 0 && (
            <div
              className="tv-alert tv-alert-info tv-mx-3 tv-mt-3 tv-mb-0"
              style={{
                backgroundColor: 'var(--surface_container_low)',
                borderColor: 'var(--accent-primary)',
                color: 'var(--text-primary)',
              }}
            >
              <i className="fas fa-info-circle me-2"></i>
              <strong>Stock data not available.</strong> Run screening or click
              "Refresh Data" to load current market data for your favorites.
            </div>
          )}

          {/* Mobile Card View - visible only on small screens */}
          <div className="md:hidden p-2 pb-24">
            {sortedFavorites.map((favorite, index) => (
              <MobileFavoriteCard
                key={favorite.id}
                favorite={favorite}
                index={index}
              />
            ))}
          </div>

          {/* Desktop Table View - hidden on mobile */}
          <div className="tv-p-0 hidden md:block">
            <div className="table-responsive favorites-table-wrap">
              <table className="tradingview-table favorites-table">
                <thead className="tradingview-table-header">
                  <tr>
                    <th
                      className="checkbox-column-header"
                      style={{
                        width: '40px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        textAlign: 'center',
                      }}
                    >
                      <input
                        type="checkbox"
                        className="tv-form-check-input"
                        checked={
                          selectedFavorites.size === sortedFavorites.length &&
                          sortedFavorites.length > 0
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title={`Select all ${sortedFavorites.length} favorites`}
                      />
                    </th>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`tradingview-th ${
                          column.sortable ? 'sortable-th' : ''
                        }`}
                        onClick={() =>
                          column.sortable && handleSort(column.key)
                        }
                        title={
                          column.sortable
                            ? `Sort by ${column.label}`
                            : undefined
                        }
                        style={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 5,
                          textAlign:
                            column.key === 'ticker' ||
                            column.key === 'entry_rationale' ||
                            column.key === 'notes'
                              ? 'left'
                              : 'right',
                        }}
                      >
                        <div
                          className="tv-flex tv-items-center tv-gap-1"
                          style={{
                            justifyContent:
                              column.key === 'ticker' ||
                              column.key === 'entry_rationale' ||
                              column.key === 'notes'
                                ? 'flex-start'
                                : 'flex-end',
                          }}
                        >
                          <span>{column.label}</span>
                          {column.sortable && getSortIcon(column.key)}
                        </div>
                      </th>
                    ))}
                    <th
                      className="tradingview-th"
                      style={{
                        width: '180px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        textAlign: 'left',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFavorites.map((favorite, index) => {
                    const isSelected = selectedFavorites.has(favorite.ticker);
                    const isCurrentRow = index === currentRowIndex;

                    return (
                      <tr
                        key={favorite.id}
                        onClick={(e) => handleRowClick(favorite, e, index)}
                        className={`tradingview-row ${
                          isSelected ? 'row-selected' : ''
                        } ${
                          analysisPanel.isOpen &&
                          analysisPanel.selectedTicker === favorite.ticker
                            ? 'row-active'
                            : ''
                        }`}
                        style={{
                          backgroundColor:
                            isSelected || isCurrentRow
                              ? 'rgba(255, 235, 59, 0.4)' // Bright yellow background for selected row
                              : analysisPanel.isOpen &&
                                  analysisPanel.selectedTicker ===
                                    favorite.ticker
                                ? 'rgba(33, 150, 243, 0.05)'
                                : 'transparent',
                          borderBottom: '1px solid var(--border-default)',
                          borderLeft:
                            isSelected || isCurrentRow
                              ? '6px solid #F57F17' // Dark yellow border to match yellow background
                              : '4px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          animation: `fadeIn 0.3s ease-out ${
                            index * 0.05
                          }s both`,
                        }}
                        onMouseEnter={(e) => {
                          if (
                            !isSelected &&
                            !isCurrentRow &&
                            (!analysisPanel.isOpen ||
                              analysisPanel.selectedTicker !== favorite.ticker)
                          ) {
                            e.currentTarget.style.backgroundColor =
                              'var(--surface_container_low)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (
                            !isSelected &&
                            !isCurrentRow &&
                            (!analysisPanel.isOpen ||
                              analysisPanel.selectedTicker !== favorite.ticker)
                          ) {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }
                        }}
                      >
                        <td
                          style={{
                            width: '40px',
                            textAlign: 'center',
                            padding: '10px 4px',
                            backgroundColor: 'var(--surface)',
                          }}
                        >
                          <input
                            type="checkbox"
                            className="tv-checkbox"
                            checked={isSelected}
                            onChange={(e) =>
                              handleSelectFavorite(
                                favorite.ticker,
                                e.target.checked
                              )
                            }
                            title={`Select ${favorite.ticker}`}
                          />
                        </td>
                        {columns.map((column) => {
                          const content = column.format
                            ? column.format(null, favorite)
                            : favorite.ticker;

                          return (
                            <td
                              key={column.key}
                              className={`tradingview-cell ${
                                column.className || ''
                              }`}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: 'var(--surface)',
                                color: 'var(--text-primary)',
                                fontSize: '0.875rem',
                                fontFamily:
                                  column.key === 'ticker'
                                    ? 'Consolas, "Courier New", monospace'
                                    : 'inherit',
                                fontWeight:
                                  column.key === 'ticker' ? '700' : '500',
                                textAlign:
                                  column.key === 'ticker' ||
                                  column.key === 'entry_rationale' ||
                                  column.key === 'notes'
                                    ? 'left'
                                    : 'right',
                                maxWidth:
                                  column.key === 'entry_rationale'
                                    ? 228
                                    : column.key === 'notes'
                                      ? 208
                                      : undefined,
                                minWidth:
                                  column.key === 'entry_rationale' ||
                                  column.key === 'notes'
                                    ? 0
                                    : undefined,
                                borderRight: '1px solid var(--border-subtle)',
                                lineHeight: '1.2',
                              }}
                            >
                              {content}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            padding: '10px 12px',
                            backgroundColor: 'var(--surface)',
                            textAlign: 'right',
                            borderRight: '1px solid var(--border-subtle)',
                            lineHeight: '1.2',
                          }}
                        >
                          <div
                            className="tv-flex tv-gap-1"
                            style={{ justifyContent: 'flex-end' }}
                          >
                            {/* Grok + Pplx removed */}
                            <button
                              className="tv-btn tv-btn-sm tv-btn-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(
                                  openPanel({
                                    ticker: favorite.ticker,
                                    source: 'favorites',
                                  })
                                ); // Let panel preserve current tab
                              }}
                              title={`Analyze ${favorite.ticker} - View Charts, TradingView & AI Analysis`}
                            >
                              <i className="fas fa-chart-line"></i>
                            </button>
                            <button
                              className="tv-btn tv-btn-sm tv-btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFavorite(favorite.ticker);
                              }}
                              title={`Remove ${favorite.ticker} from favorites`}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Action Modal */}
      <StockActionModal
        ticker={actionModal.ticker}
        show={actionModal.show}
        onHide={() => setActionModal({ ticker: '', show: false })}
      />

      {/* Color Change Modal */}
      {colorModal.show && (
        <div
          className="tv-modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1060,
          }}
          tabIndex={-1}
        >
          <div className="tv-modal-dialog tv-modal-sm">
            <div
              className="tv-modal-content"
              style={{
                backgroundColor: 'var(--surface_container)',
                color: 'var(--text-primary)',
              }}
            >
              <div
                className="tv-modal-header"
                style={{ borderBottomColor: 'var(--border-default)' }}
              >
                <h5 className="tv-modal-title tv-text-primary">
                  <i className="fas fa-palette"></i> Change Color
                </h5>
                <button
                  type="button"
                  className="tv-btn-close"
                  title="Close color modal"
                  aria-label="Close color modal"
                  onClick={() =>
                    setColorModal({
                      show: false,
                      favorite: null,
                      newColor: 'blue',
                    })
                  }
                ></button>
              </div>
              <div className="tv-modal-body">
                <div className="tv-mb-3">
                  <label className="tv-form-label tv-text-secondary">
                    Stock Ticker
                  </label>
                  <input
                    type="text"
                    className="tv-form-control"
                    value={colorModal.favorite?.ticker || ''}
                    title="Selected stock ticker"
                    aria-label="Selected stock ticker"
                    readOnly
                    style={{
                      backgroundColor: 'var(--surface_container_low)',
                      borderColor: 'var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div className="tv-mb-3">
                  <label className="tv-form-label tv-text-secondary">
                    Priority Color
                  </label>
                  <select
                    className="tv-form-select"
                    value={colorModal.newColor}
                    title="Select favorite priority color"
                    aria-label="Select favorite priority color"
                    onChange={(e) =>
                      setColorModal((prev) => ({
                        ...prev,
                        newColor: e.target.value as FavoriteColor,
                      }))
                    }
                    style={{
                      backgroundColor: 'var(--surface_container_low)',
                      borderColor: 'var(--border-default)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option
                      value="blue"
                      style={{
                        backgroundColor: 'var(--surface_container_low)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      🔵 Blue - Normal Priority
                    </option>
                    <option
                      value="green"
                      style={{
                        backgroundColor: 'var(--surface_container_low)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      🟢 Green - High Priority
                    </option>
                    <option
                      value="yellow"
                      style={{
                        backgroundColor: 'var(--surface_container_low)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      🟡 Yellow - Medium Priority
                    </option>
                    <option
                      value="red"
                      style={{
                        backgroundColor: 'var(--surface_container_low)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      🔴 Red - Critical Priority
                    </option>
                    <option
                      value="purple"
                      style={{
                        backgroundColor: 'var(--surface_container_low)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      🟣 Purple - Special Watch
                    </option>
                  </select>
                </div>
              </div>
              <div
                className="tv-modal-footer"
                style={{ borderTopColor: 'var(--border-default)' }}
              >
                <button
                  type="button"
                  className="tv-btn tv-btn-secondary"
                  onClick={() =>
                    setColorModal({
                      show: false,
                      favorite: null,
                      newColor: 'blue',
                    })
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tv-btn tv-btn-primary"
                  onClick={handleColorChange}
                  style={{
                    backgroundColor: 'var(--accent-primary)',
                    color: '#1a1a1a',
                  }}
                >
                  <i className="fas fa-palette"></i> Update Color
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesList;
